import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  money,
  fmtDate,
  containerAuditRefs,
} from "../../pages/invoices/invoiceUtils";
import type {
  ClientSnapshot,
  InvoiceLine,
  InvoiceType,
  OrgSnapshot,
  VatCategory,
  VatGroup,
} from "../../types/invoice";

/**
 * The invoice as it will be sent, rendered in HTML.
 *
 * The same role `CmrPreview` plays for a shipment: the document sits beside the
 * form and redraws as the form is edited, so the person composing it is looking
 * at the thing the client will receive rather than at the fields that produce
 * it. Nothing here is editable and nothing is fetched — it is a pure projection
 * of whatever draft state it is handed.
 *
 * Its layout deliberately tracks `services/invoicePdfService.js` section for
 * section — org header, billed-to, line table, VAT summary, container
 * references, payment details — because the two must agree: this is what the
 * user approves and that is what actually goes out. When a section is added to
 * one, add it to the other.
 *
 * The appended specification pages are NOT drawn here. They are frozen at lock
 * time from the live shipments and containers, so before a lock there is
 * nothing to render faithfully; the editor states that they will be appended
 * instead of guessing at their content.
 */
export interface InvoicePreviewProps {
  invoiceNumber?: string;
  invoiceType?: InvoiceType;
  org?: OrgSnapshot | null;
  client?: ClientSnapshot | null;
  invoiceDate?: string;
  dueDate?: string;
  paymentTerms?: number;
  poReference?: string;
  costCentre?: string;
  projectCode?: string;
  lines: InvoiceLine[];
  vatGroups: VatGroup[];
  total: number;
  /** Shown in the credit-note header, when this credits an existing invoice. */
  referencesInvoiceNumber?: string;
  /** Whether specification pages will be appended to the generated PDF. */
  includeSpecification?: boolean;
  specificationCount?: number;
}

/** "street, city" / "postal country" — the two address lines the PDF prints. */
const addressLines = (addr?: {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}) =>
  [
    [addr?.street, addr?.city].filter(Boolean).join(", "),
    [addr?.postalCode, addr?.country].filter(Boolean).join(" "),
  ].filter(Boolean);

const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  invoiceNumber,
  invoiceType = "invoice",
  org,
  client,
  invoiceDate,
  dueDate,
  paymentTerms,
  poReference,
  costCentre,
  projectCode,
  lines,
  vatGroups,
  total,
  referencesInvoiceNumber,
  includeSpecification,
  specificationCount = 0,
}) => {
  const { t } = useTranslation("invoicing");
  const isCredit = invoiceType === "credit_note";

  const vatPct = (c: VatCategory) =>
    c === "24" ? "24%" : c === "0" ? "0%" : "—";

  // One row per container billed, not per line — the same list the PDF's
  // references block builds, so the two agree.
  const containerRefs = useMemo(
    () =>
      containerAuditRefs(lines).map((r) => ({
        key: r.key,
        lineNumber: r.lineNumber,
        detail: [
          r.containerNumber
            ? t("preview.refContainer", { value: r.containerNumber })
            : "",
          r.orderNumber ? t("preview.refOrder", { value: r.orderNumber }) : "",
          r.orderReference
            ? t("preview.refClientRef", { value: r.orderReference })
            : "",
          r.bookingRef
            ? t("preview.refBooking", { value: r.bookingRef })
            : "",
        ]
          .filter(Boolean)
          .join(" · "),
      })),
    [lines, t],
  );

  const orgAddress = addressLines(org?.address);
  const clientAddress = addressLines(client?.address);

  return (
    <div className="text-[#0B0F0E] break-words">
      {/* Header — org (left) · title + meta (right) */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {org?.logoUrl && (
            <img
              src={org.logoUrl}
              alt=""
              className="h-10 w-auto object-contain mb-2"
            />
          )}
          <div className="text-[14px] font-bold truncate">
            {org?.legalName || "—"}
          </div>
          <div className="text-[10px] text-[#6b6b67] leading-[1.5] mt-0.5">
            {orgAddress.map((l) => (
              <div key={l}>{l}</div>
            ))}
            {org?.registryNumber && (
              <div>{t("preview.reg", { value: org.registryNumber })}</div>
            )}
            {org?.vatNumber && (
              <div>{t("preview.vat", { value: org.vatNumber })}</div>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[20px] font-bold tracking-tight text-[#0C447C] leading-none">
            {isCredit ? t("preview.titleCreditNote") : t("preview.titleInvoice")}
          </div>
          <div className="text-[13px] font-bold mt-1">
            {invoiceNumber || t("preview.numberPending")}
          </div>
          <div className="text-[10px] text-[#6b6b67] mt-2 space-y-0.5">
            <div>
              {t("preview.issueDate")}{" "}
              <span className="font-semibold text-[#0B0F0E]">
                {fmtDate(invoiceDate)}
              </span>
            </div>
            <div>
              {t("preview.dueDate")}{" "}
              <span className="font-semibold text-[#0B0F0E]">
                {fmtDate(dueDate)}
              </span>
            </div>
            <div>
              {t("preview.terms")}{" "}
              <span className="font-semibold text-[#0B0F0E]">
                {t("preview.net", { days: paymentTerms ?? 14 })}
              </span>
            </div>
            {isCredit && referencesInvoiceNumber && (
              <div>
                {t("preview.againstInvoice")}{" "}
                <span className="font-semibold text-[#0B0F0E]">
                  {referencesInvoiceNumber}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-[#d9d7d0] my-3" />

      {/* Billed to */}
      <div className="text-[9px] font-bold tracking-wide text-[#8A9590] uppercase">
        {t("preview.billedTo")}
      </div>
      <div className="text-[12px] font-bold mt-1">
        {client?.companyName || "—"}
      </div>
      <div className="text-[10px] text-[#6b6b67] leading-[1.5]">
        {client?.registerationCode && (
          <div>{t("preview.reg", { value: client.registerationCode })}</div>
        )}
        {client?.vatNumber && (
          <div>{t("preview.vat", { value: client.vatNumber })}</div>
        )}
        {clientAddress.map((l) => (
          <div key={l}>{l}</div>
        ))}
        {client?.email && <div>{client.email}</div>}
      </div>

      {(poReference || costCentre || projectCode) && (
        <div className="text-[10px] text-[#6b6b67] mt-2 flex flex-wrap gap-x-3">
          {poReference && (
            <span>{t("preview.poRef", { value: poReference })}</span>
          )}
          {costCentre && (
            <span>{t("preview.costCentre", { value: costCentre })}</span>
          )}
          {projectCode && (
            <span>{t("preview.projectCode", { value: projectCode })}</span>
          )}
        </div>
      )}

      {/* Lines */}
      <table className="w-full mt-4 table-fixed">
        <thead>
          <tr className="bg-[#F5F4F0] text-[9px] uppercase tracking-wide text-[#8A9590]">
            <th className="text-left font-bold px-1.5 py-1">
              {t("preview.colDescription")}
            </th>
            <th className="text-right font-bold px-1.5 py-1 w-[36px]">
              {t("preview.colVat")}
            </th>
            <th className="text-right font-bold px-1.5 py-1 w-[32px]">
              {t("preview.colQty")}
            </th>
            <th className="text-right font-bold px-1.5 py-1 w-[62px]">
              {t("preview.colUnitPrice")}
            </th>
            <th className="text-right font-bold px-1.5 py-1 w-[66px]">
              {t("preview.colTotal")}
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => {
            const ref = [
              l.shipmentNumber || l.workNumber || l.containerLineNumber,
              l.containerNumber,
              l.sourceServiceRef,
            ]
              .filter(Boolean)
              .join(" · ");
            const lineTotal =
              l.lineTotal != null
                ? Number(l.lineTotal)
                : (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
            return (
              <tr
                key={l._id || i}
                className="border-b border-[#eceae4] align-top"
              >
                <td className="px-1.5 py-1.5 text-[10px]">
                  <div className="break-words">
                    {l.description || (
                      <span className="text-[#b9b9b3]">
                        {t("preview.untitledLine")}
                      </span>
                    )}
                  </div>
                  {ref && (
                    <div className="text-[9px] text-[#9b9b96] mt-0.5 break-words">
                      {ref}
                    </div>
                  )}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] text-right">
                  {vatPct(l.vatCategory)}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] text-right">
                  {l.quantity}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] text-right">
                  {money(l.unitPrice)}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] text-right font-semibold">
                  {money(lineTotal)}
                </td>
              </tr>
            );
          })}
          {lines.length === 0 && (
            <tr>
              <td
                colSpan={5}
                className="px-1.5 py-6 text-center text-[10px] text-[#b9b9b3]"
              >
                {t("preview.noLines")}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* VAT summary + total */}
      <div className="flex justify-end mt-3">
        <div className="w-[58%] text-[10px]">
          {vatGroups.map((g) => (
            <React.Fragment key={g.vatCategory}>
              <div className="flex justify-between text-[#6b6b67]">
                <span>
                  {g.vatCategory === "NO_TAX"
                    ? t("vat.subtotalNoVat")
                    : g.vatCategory === "24"
                      ? t("vat.subtotal24")
                      : t("vat.subtotal0")}
                </span>
                <span>{money(g.subtotal)}</span>
              </div>
              <div className="flex justify-between text-[#6b6b67]">
                <span>
                  {g.vatCategory === "NO_TAX"
                    ? t("vat.outsideScope")
                    : g.vatCategory === "24"
                      ? t("vat.vat24")
                      : t("vat.vat0")}
                </span>
                <span>{money(g.vatAmount)}</span>
              </div>
            </React.Fragment>
          ))}
          <div className="flex justify-between border-t border-[#0B0F0E] mt-1.5 pt-1.5 text-[12px] font-bold">
            <span>
              {isCredit ? t("preview.totalCreditEur") : t("preview.totalEur")}
            </span>
            <span className={isCredit ? "text-[#A32D2D]" : ""}>
              {money(total)}
            </span>
          </div>
        </div>
      </div>

      {/* Container references — the audit block */}
      {containerRefs.length > 0 && (
        <div className="mt-4">
          <div className="text-[9px] font-bold tracking-wide text-[#8A9590] uppercase">
            {t("preview.containerReferences")}
          </div>
          <div className="mt-1 space-y-0.5">
            {containerRefs.map((r) => (
              <div key={r.key} className="flex gap-2 text-[9px]">
                <span className="font-bold shrink-0 w-[54px]">
                  {r.lineNumber}
                </span>
                <span className="text-[#6b6b67] break-words">{r.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-[#d9d7d0] mt-4 pt-2 text-[9px] text-[#6b6b67] space-y-1">
        {!isCredit && org?.iban && (
          <div className="break-words">
            {t("preview.paymentReference", {
              number: invoiceNumber || "—",
              iban: org.iban,
            })}
            {org.bankName ? `   ·   ${org.bankName}` : ""}
          </div>
        )}
        {org?.invoiceFooter && (
          <div className="break-words">{org.invoiceFooter}</div>
        )}
        {includeSpecification && specificationCount > 0 && (
          <div className="italic text-[#8A9590]">
            {t("preview.specAppended", { count: specificationCount })}
          </div>
        )}
        <div className="text-right text-[#b9b9b3]">
          {t("preview.generatedBy")}
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
